"use strict";
/**
 * General Ledger posting engine (pure, dependency-free).
 *
 * Implements the double-entry mechanics required by the accounting model
 * (`.kiro/specs/accounting-model/requirements.md`, Requirements 1–3, 9):
 *
 *  - every posting is balanced (Σ debit = Σ credit) in integer pence;
 *  - a posting has at least two lines;
 *  - every line references a nominal code;
 *  - postings are idempotent — re-posting the same source document produces
 *    the same deterministic `entryId`s, so a retry overwrites rather than
 *    duplicates.
 *
 * This module is intentionally free of any AWS/DynamoDB dependency: it builds
 * and validates the ledger rows to persist. The calling Lambda (which has the
 * aws-sdk dependency) performs the actual write. This keeps the money-critical
 * mechanics unit-testable in isolation and reusable by every posting source
 * (financial records, settlements, manual journals, import).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildReversal = exports.buildPosting = exports.validatePosting = exports.PostingError = void 0;
/** Error thrown when a posting violates a double-entry invariant. The message
 *  is safe to log (no monetary PII beyond aggregate totals). */
class PostingError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PostingError';
    }
}
exports.PostingError = PostingError;
const isInteger = (n) => typeof n === 'number' && Number.isInteger(n);
/**
 * Validates the double-entry invariants for a set of lines. Throws
 * `PostingError` on any violation; returns silently when valid.
 *
 * Invariants (accounting-model Requirement 2):
 *   - at least two lines;
 *   - every amount is a non-negative integer (pence);
 *   - each line is single-sided (exactly one of debit/credit is non-zero);
 *   - every line has a nominal code;
 *   - Σ debit === Σ credit.
 */
function validatePosting(lines) {
    if (!Array.isArray(lines) || lines.length < 2) {
        throw new PostingError('A posting must have at least two lines');
    }
    let totalDebit = 0;
    let totalCredit = 0;
    for (const line of lines) {
        if (!line.nominalCode) {
            throw new PostingError('Every posting line must reference a nominalCode');
        }
        if (!isInteger(line.debit) || !isInteger(line.credit)) {
            throw new PostingError(`Line amounts must be integer pence (nominal ${line.nominalCode})`);
        }
        if (line.debit < 0 || line.credit < 0) {
            throw new PostingError(`Line amounts must be non-negative (nominal ${line.nominalCode})`);
        }
        const debitSide = line.debit > 0;
        const creditSide = line.credit > 0;
        if (debitSide === creditSide) {
            // both zero, or both non-zero
            throw new PostingError(`Each line must be single-sided: exactly one of debit/credit is non-zero (nominal ${line.nominalCode})`);
        }
        totalDebit += line.debit;
        totalCredit += line.credit;
    }
    if (totalDebit !== totalCredit) {
        throw new PostingError(`Unbalanced posting: debits ${totalDebit} !== credits ${totalCredit} (pence)`);
    }
}
exports.validatePosting = validatePosting;
/**
 * Resolves the posting group id. Prefers an explicit `postingId`, otherwise
 * derives a deterministic id from the source document so retries are idempotent.
 */
function resolvePostingId(input) {
    if (input.postingId)
        return input.postingId;
    if (input.source)
        return `${input.source.type}#${input.source.id}`;
    throw new PostingError('A posting requires either an explicit postingId or a source document for idempotency');
}
/**
 * Builds a balanced, idempotent posting ready to persist to the LedgerTable.
 *
 * Each returned row carries a deterministic `entryId` of the form
 * `{postingId}#{index}` so that re-posting the same source document overwrites
 * the same rows rather than creating duplicates (accounting-model Requirement 3,
 * property P3-NoOrphanCharge).
 *
 * @throws PostingError if the lines are not balanced/valid or `ownerId` /
 *   `buildingId` / `date` are missing.
 */
function buildPosting(input, now = new Date().toISOString()) {
    if (!input.ownerId)
        throw new PostingError('ownerId is required');
    if (!input.buildingId)
        throw new PostingError('buildingId is required');
    if (!input.date)
        throw new PostingError('date is required');
    validatePosting(input.lines);
    const postingId = resolvePostingId(input);
    const rows = input.lines.map((line, index) => ({
        buildingId: input.buildingId,
        entryId: `${postingId}#${index}`,
        ownerId: input.ownerId,
        postingId,
        date: input.date,
        nominalCode: line.nominalCode,
        category: line.category,
        debit: line.debit,
        credit: line.credit,
        description: line.description ?? input.description,
        reference: input.reference,
        sourceType: input.source?.type,
        sourceId: input.source?.id,
        createdAt: now,
        createdBy: input.createdBy,
    }));
    return { postingId, rows };
}
exports.buildPosting = buildPosting;
/**
 * Builds the reversing posting for an existing posting: debits and credits are
 * swapped, and the reversal is itself idempotent (deterministic entryIds under
 * a `reversal#{postingId}` group). Corrections are made by reversal, never by
 * mutating posted rows (accounting-model Requirement 1.4).
 */
function buildReversal(original, input, now = new Date().toISOString()) {
    const postingId = `reversal#${original.postingId}`;
    const rows = original.rows.map((row, index) => ({
        ...row,
        entryId: `${postingId}#${index}`,
        postingId,
        debit: row.credit,
        credit: row.debit,
        date: input.date,
        description: input.description ?? `Reversal of ${original.postingId}`,
        reference: input.reference ?? row.reference,
        createdAt: now,
        createdBy: input.createdBy,
    }));
    return { postingId, rows };
}
exports.buildReversal = buildReversal;
